/**
 * FlyDaysStrip — a small Sunday-to-Saturday strip for a carrier that does not
 * fly every day: the days it flies are filled, the leg's own weekday is
 * outlined, and when it does not fly your date we say so and offer the nearest
 * dates it does fly.
 */
import type { PlanFlyDays } from "@/lib/fareOptionsTypes";
import { flyDaysView } from "./fareLadderView";

interface FlyDaysStripProps {
  flyDays: PlanFlyDays;
  /** The leg's date (YYYY-MM-DD). */
  legDate: string | null | undefined;
}

export default function FlyDaysStrip({ flyDays, legDate }: FlyDaysStripProps) {
  const v = flyDaysView(flyDays, legDate);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[11px]">
      <span className="text-muted-foreground">{v.heading}</span>
      <span role="img" aria-label={v.summary} className="inline-flex items-center gap-0.5">
        {v.cells.map((c) => (
          <span
            key={c.index}
            aria-hidden="true"
            title={c.name + (c.flies ? ": flies" : ": does not fly") + (c.isLegDay ? " (your date)" : "")}
            className={
              "inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-medium " +
              (c.flies
                ? "bg-[hsl(var(--primary))]/[0.16] text-[hsl(var(--accent-foreground))] "
                : "text-muted-foreground/70 ") +
              (c.isLegDay ? "ring-1 ring-inset ring-foreground" : "")
            }
          >
            {c.letter}
          </span>
        ))}
      </span>
      {v.notOnDate && (
        <span className="font-medium text-amber-700 dark:text-amber-400">Does not fly your date</span>
      )}
      {v.nearest.length > 0 && (
        <span className="text-muted-foreground">
          {v.notOnDate ? "Nearest: " : "Also: "}
          {v.nearest.map((n, i) => (
            <span key={n.date}>
              {i > 0 && ", "}
              <span className="text-foreground">{n.label}</span>
              {n.price && <span className="font-mono tabular-nums"> {n.price}</span>}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
